export const SKU_AVAILABILITY_STATUS_VALUES = ["ga", "beta", "coming_soon", "under_consideration"] as const;
export type SkuAvailabilityStatus = (typeof SKU_AVAILABILITY_STATUS_VALUES)[number];

export const APP_STATUS_VALUES = ["installed", "in_free_trial", "uninstalled", "never_installed"] as const;
export type AppStatus = (typeof APP_STATUS_VALUES)[number];

/** Legacy "Growth Plan" SKU; an active billing item for this SKU grants access to all apps. */
export const LEGACY_GROWTH_PLAN_SLUG = "growth-plan";

/** Legacy Workforce Management SKU; an active billing item also grants Scheduling, Time Tracking, and Field Reporting. */
export const LEGACY_WORKFORCE_MANAGEMENT_SLUG = "workforce-management";

export const BENADMIN_SLUG = "benefits-administration";
export const ACA_SLUG = "aca-compliance";
export const COBRA_SLUG = "cobra-compliance";
export const EN_SLUG = "employee-navigator";
export const RETIREMENT_SLUG = "401k-integration";
export const API_SLUG = "miter-api";
export const SAFETY_SLUG = "safety";
export const PROJECT_INTELLIGENCE_SLUG = "project-intelligence";

export const BENEFIT_SKU_SLUG_VALUES = [
  BENADMIN_SLUG,
  ACA_SLUG,
  COBRA_SLUG,
  EN_SLUG,
  RETIREMENT_SLUG,
] as const;

export type BenefitSkuSlug = (typeof BENEFIT_SKU_SLUG_VALUES)[number];

/** Priced as a pass-through of the vendor's fee, so the App Shop doesn't offer volume discounts. */
export const PASS_THROUGH_FEE_SKU_SLUGS: readonly string[] = [EN_SLUG, RETIREMENT_SLUG];

/** Install-time prerequisites by SKU slug. Only gates new installs, so existing standalone billing items keep access. */
export const PREREQUISITE_SKU_SLUGS_BY_SLUG: Record<string, readonly string[]> = {
  "learning-all-access": ["learning"],
  [ACA_SLUG]: [BENADMIN_SLUG],
  [COBRA_SLUG]: [BENADMIN_SLUG],
};

/** Reverse of PREREQUISITE_SKU_SLUGS_BY_SLUG: dependents that get uninstalled alongside their prerequisite. */
export const DEPENDENT_SKU_SLUGS_BY_PREREQUISITE_SLUG: Record<string, readonly string[]> = Object.entries(
  PREREQUISITE_SKU_SLUGS_BY_SLUG
).reduce<Record<string, string[]>>((acc, [slug, prerequisiteSlugs]) => {
  for (const prerequisiteSlug of prerequisiteSlugs) {
    acc[prerequisiteSlug] = [...(acc[prerequisiteSlug] ?? []), slug];
  }
  return acc;
}, {});

export type InstallReasonOption = { value: string; label: string };

/**
 * Install-reason selections keyed by SKU slug. A slug present in this map requires the customer to
 * pick a reason before installing, and each slug defines its own unique set of options. An option
 * with value "other" reveals a free-text field.
 */
export const INSTALL_REASONS_BY_SLUG: Record<string, readonly InstallReasonOption[]> = {
  [API_SLUG]: [
    { value: "integrate_erp", label: "Integrate with our ERP / accounting system" },
    { value: "sync_third_party", label: "Sync data with a third-party tool" },
    { value: "custom_reporting", label: "Build custom reports or dashboards" },
    { value: "no_reason", label: "No specific reason" },
    { value: "other", label: "Other" },
  ],
};

/** Flat reason value → label lookup across all slugs, for rendering a stored reason (e.g. install notifications). */
export const INSTALL_REASON_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(INSTALL_REASONS_BY_SLUG).flatMap((options) =>
    options.map(({ value, label }) => [value, label])
  )
);

export const INSTALL_INTENT_VALUES = [
  "exploring",
  "wants_demo",
  "ready_wants_help",
  "ready_self_serve",
  "other",
] as const;
export type InstallIntent = (typeof INSTALL_INTENT_VALUES)[number];

type InstallIntentConfig = {
  label: string;
  /** Compact label for internal reporting, where the customer-facing sentence is too long for a column. */
  shortLabel: string;
  /** Shown to the customer under their selection, so the commitment we make is visible before they install. */
  nextStep: string;
  /** Team that picks up the install, surfaced in the App Shop Slack feed. */
  routesTo: string;
};

/** Required single-choice disposition captured on every App Shop install. */
export const INSTALL_INTENTS = {
  exploring: {
    label: "I'd like to explore on my own for now.",
    shortLabel: "Just exploring",
    nextStep: "Great! Happy exploring. Don't hesitate to reach out if you have feedback or need some help.",
    routesTo: "Account Management",
  },
  wants_demo: {
    label: "I would like to set up time for a quick demo of this app's features.",
    shortLabel: "Wants a demo",
    nextStep:
      "Great! Someone from Miter will reach out shortly to set up time to give you an overview of this app.",
    routesTo: "Account Management",
  },
  ready_wants_help: {
    label: "I am ready to start using this app and I would like someone to help me get started.",
    shortLabel: "Wants setup help",
    nextStep: "Great! Someone from Miter will reach out shortly to set up time to get started.",
    routesTo: "Launch",
  },
  ready_self_serve: {
    label: "I am ready to start using this app, but I prefer to set it up myself.",
    shortLabel: "Self-serve setup",
    nextStep:
      "Great! Someone from Miter will reach out shortly to introduce themselves and help with any questions if you get stuck.",
    routesTo: "Launch",
  },
  other: {
    label: "Other",
    shortLabel: "Other",
    nextStep: "Our team will review your message and reach out to you directly if necessary.",
    routesTo: "Triage from this message",
  },
} satisfies Record<InstallIntent, InstallIntentConfig>;

/** Intent choices in display order. */
export const INSTALL_INTENT_OPTIONS: { value: InstallIntent; label: string }[] = INSTALL_INTENT_VALUES.map(
  (value) => ({ value, label: INSTALL_INTENTS[value].label })
);
